import { type RootState } from "@/store";

import { createAsyncThunk,createSlice } from "@reduxjs/toolkit";
type AuthenticatedRequestFn = (url: string, options?: RequestInit) => Promise<Response>;

interface MetaPayload {
  organization: {
    id: string;
    name: string;
    org_id: string;
  } | null;
  user: {
    id: string;
    name: string;
  } | null;
}

interface FetchMetaArgs {
  makeRequest?: AuthenticatedRequestFn;
}

// Define the initial state
interface MetaState {
  organization: {
    id: string;
    name: string;
    org_id:string;
  } | null;
  user: {
    id: string;
    name: string;
  } | null;
  loading: boolean;
  error: string | null;
}

const initialState: MetaState = {
  organization: null,
  user: null,
  loading: false,
  error: null,
};

// Async thunks for API calls
export const fetchMeta = createAsyncThunk("meta/fetchMeta", async (args?: FetchMetaArgs) => {
  const requester = args?.makeRequest ?? fetch;
  const response = await requester("/.netlify/functions/meta-get");
  if (!response.ok) {
    throw new Error("Failed to fetch metadata");
  }
  return (await response.json()) as MetaPayload;
});

export const updateMeta = createAsyncThunk(
  "meta/updateMeta",
  async ({ meta, makeRequest }: { meta: { organization: { id: string; name: string }; user: { id: string; name: string } }; makeRequest?: AuthenticatedRequestFn }) => {
    const requester = makeRequest ?? fetch;
    const response = await requester("/.netlify/functions/meta-update", {
      method: "PUT",
      body: JSON.stringify(meta),
    });
    if (!response.ok) {
      throw new Error("Failed to update metadata");
    }
    return (await response.json()) as MetaPayload;
  }
);

// Create the slice
const metaSlice = createSlice({
  name: "meta",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMeta.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMeta.fulfilled, (state, action) => {
        state.loading = false;
        state.organization = action.payload.organization;
        state.user = action.payload.user;
      })
      .addCase(fetchMeta.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch metadata";
      })
      .addCase(updateMeta.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMeta.fulfilled, (state, action) => {
        state.loading = false;
        state.organization = action.payload.organization;
        state.user = action.payload.user;
      })
      .addCase(updateMeta.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to update metadata";
      });
  },
});

// Selectors
export const selectOrganization = (state: RootState) => state.meta.organization;
export const selectUser = (state: RootState) => state.meta.user;
export const selectMetaLoading = (state: RootState) => state.meta.loading;
export const selectMetaError = (state: RootState) => state.meta.error;

export default metaSlice.reducer;
